import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Receipt,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import authService from '../services/authService';
import purchaseService from '../services/purchaseService';

const PurchaseReceiptDialog = ({
  open,
  mode = 'create', // create, edit, view
  onClose,
  onSubmit,
  purchaseOrder = null,
  initialData = null,
  selectedClientId = null,
  selectedClient = null
}) => {
  const isViewMode = mode === 'view';
  const [formData, setFormData] = useState({
    receipt_date: new Date().toISOString().split('T')[0],
    receipt_note: '',
    receipt_status: 'partial', // partial, complete
    inspector_name: '',
    receipt_items: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({
    totalReceived: 0,
    totalOrdered: 0,
    isComplete: false
  });

  const calculateValidation = useCallback(() => {
    if (!formData.receipt_items || !formData.receipt_items.length) return;

    const totalOrdered = formData.receipt_items.reduce((sum, item) => sum + (item.ordered_quantity || 0), 0);
    const totalReceived = formData.receipt_items.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
    const isComplete = formData.receipt_items.every(item =>
      (item.received_quantity || 0) >= (item.ordered_quantity || 0)
    );

    setValidation({
      totalOrdered,
      totalReceived,
      isComplete,
      fulfillmentRate: totalOrdered > 0 ? (totalReceived / totalOrdered * 100) : 0
    });
  }, [formData.receipt_items]);

  const initializeReceiptData = useCallback(async () => {
    console.log('=== initializeReceiptData called ===');
    console.log('initialData:', initialData);
    console.log('purchaseOrder:', purchaseOrder);

    if (initialData) {
      console.log('Processing initialData (view/edit mode)');
      let receiptItems = initialData.receipt_items || [];
      console.log('Initial receiptItems from receipt_items:', receiptItems);

      if (!receiptItems.length && initialData.received_items) {
        console.log('Transforming from received_items:', initialData.received_items);
        receiptItems = initialData.received_items;
      }

      if (!receiptItems.length && initialData.items) {
        console.log('Transforming from items:', initialData.items);
        receiptItems = initialData.items;
      }

      if (receiptItems.length > 0) {
        receiptItems = receiptItems.map(item => {
          const receivedQty = item.quantity_received || item.received_quantity || 0;
          const orderedQty = item.quantity_ordered || item.ordered_quantity || receivedQty;

          return {
            product_id: item.product_id,
            _id: item._id || null,  // MongoDB ObjectId
            product_name: item.product_name || item.name,
            product_code: item.product_code,
            ordered_quantity: orderedQty,
            received_quantity: receivedQty,
            previously_received: 0,
            remaining_quantity: orderedQty - receivedQty,
            unit_cost: item.unit_cost || 0,
            condition: item.condition || 'good',
            location: item.location || '',
            batch_number: item.batch_number || '',
            expiry_date: item.expiry_date || '',
            notes: item.notes || '',
            line_total: receivedQty * (item.unit_cost || 0)
          };
        });
      }

      console.log('Final receiptItems to be set:', receiptItems);
      console.log('receiptItems length:', receiptItems.length);

      const formDataToSet = {
        ...initialData,
        receipt_date: new Date(initialData.receipt_date).toISOString().split('T')[0],
        receipt_items: receiptItems
      };
      console.log('Setting formData:', formDataToSet);

      setFormData(formDataToSet);
    } else {
      if (!purchaseOrder || (!purchaseOrder.products && !purchaseOrder.items)) {
        console.error('Purchase order or products not available:', purchaseOrder);
        setFormData(prev => ({
          ...prev,
          receipt_items: []
        }));
        setErrors({ general: '采购订单数据不可用，无法创建收货记录' });
        return;
      }

      const orderItems = purchaseOrder.products || purchaseOrder.items || [];
      let receiptsResp = null;
      try {
        receiptsResp = await purchaseService.getReceipts(purchaseOrder._id);
      } catch (err) {
        receiptsResp = { receipts: purchaseOrder.receipts || [] };
      }
      const receipts = Array.isArray(receiptsResp) ? receiptsResp : (receiptsResp.receipts || []);

      const totalsMap = new Map();
      const addToTotals = (key, qty) => {
        if (!key) return;
        const k = String(key);
        const prev = totalsMap.get(k) || 0;
        totalsMap.set(k, prev + (parseFloat(qty) || 0));
      };
      receipts.forEach(r => {
        const items = r.received_items || r.receipt_items || r.items || [];
        items.forEach(ri => {
          const qty = ri.quantity_received ?? ri.received_quantity ?? 0;
          const uniqueKeys = new Set([
            ri.product_code ? String(ri.product_code) : null,
            ri.product_id ? String(ri.product_id) : null,
          ].filter(Boolean));
          uniqueKeys.forEach(k => addToTotals(k, qty));
        });
      });

      const receiptItems = orderItems.map(product => {
        const orderedQty = parseFloat(product.quantity) || 0;
        const key1 = product.product_code ? String(product.product_code) : null;
        const key2 = product.product_id ? String(product.product_id) : (product._id ? String(product._id) : null);
        const keys = new Set([key1, key2].filter(Boolean));
        let totalReceivedQty = 0;
        keys.forEach(k => { totalReceivedQty += (totalsMap.get(k) || 0); });
        const remainingQty = Math.max(0, orderedQty - totalReceivedQty);

        return {
          product_id: product.product_id,
          product_object_id: product.product_object_id || product._id || null,
          _id: product.product_object_id || product._id || null,
          product_name: product.product_name || product.name,
          product_code: product.product_code || '',
          ordered_quantity: orderedQty,
          received_quantity: 0,
          previously_received: totalReceivedQty,
          remaining_quantity: remainingQty,
          unit_cost: product.unit_price || product.unit_cost || 0,
          condition: 'good',
          location: '',
          batch_number: '',
          expiry_date: '',
          notes: ''
        };
      });

      setFormData(prev => ({ ...prev, receipt_items: receiptItems }));
    }
    setErrors({});
  }, [initialData, purchaseOrder]);

  useEffect(() => {
    if (open && (purchaseOrder || initialData)) {
      (async () => {
        await initializeReceiptData();
      })();
    }
  }, [open, purchaseOrder, initialData, initializeReceiptData]);

  useEffect(() => {
    if (open) {
      const currentUser = authService.getCurrentUser();
      console.log('Current user for inspector:', currentUser);
      if (currentUser) {
        const inspectorName = currentUser.username || currentUser.email || 'Unknown User';
        console.log('Setting inspector_name to:', inspectorName);
        setFormData(prev => ({
          ...prev,
          inspector_name: inspectorName
        }));
      }
    }
  }, [open]);

  useEffect(() => {
    calculateValidation();
  }, [formData.receipt_items, calculateValidation]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.receipt_items];
    newItems[index][field] = value;

    if (field === 'received_quantity' || field === 'unit_cost') {
      const quantity = parseFloat(newItems[index].received_quantity) || 0;
      const cost = parseFloat(newItems[index].unit_cost) || 0;
      newItems[index].line_total = quantity * cost;
    }

    setFormData(prev => ({
      ...prev,
      receipt_items: newItems
    }));

    const errorKey = `item_${field}_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: null }));
    }
  };

  const handleQuickFill = (type) => {
    const newItems = [...formData.receipt_items];

    newItems.forEach(item => {
      if (type === 'full') {
        item.received_quantity = item.remaining_quantity || 0;
      } else if (type === 'zero') {
        item.received_quantity = 0;
      }

      const quantity = parseFloat(item.received_quantity) || 0;
      const cost = parseFloat(item.unit_cost) || 0;
      item.line_total = quantity * cost;
    });

    setFormData(prev => ({
      ...prev,
      receipt_items: newItems
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.receipt_date) {
      newErrors.receipt_date = '请选择收货日期';
    }

    if (!formData.inspector_name || formData.inspector_name.trim() === '') {
      newErrors.inspector_name = '验收人员信息缺失，请刷新页面重试';
    }

    if (!formData.receipt_items || formData.receipt_items.length === 0) {
      newErrors.receipt_items = '采购订单数据不可用，无法创建收货记录';
    } else {
      let hasReceivedItems = false;
      formData.receipt_items.forEach((item, index) => {
        const receivedQty = parseFloat(item.received_quantity) || 0;

        if (receivedQty > 0) {
          hasReceivedItems = true;

          if (!item.condition) {
            newErrors[`item_condition_${index}`] = '请选择货物状态';
          }

          if (!item.location || item.location.trim() === '') {
            newErrors[`item_location_${index}`] = '仓库位置是必填项';
          }
        }
      });

      if (!hasReceivedItems) {
        newErrors.receipt_items = '至少需要收货一个产品';
      }
    }

    const effectiveClientId = selectedClientId || purchaseOrder?.client_ref || purchaseOrder?.client_id;
    const poType = purchaseOrder?.po_type;
    const hasSupplier = purchaseOrder?.supplier_id || purchaseOrder?.supplier_name;

    // 物料采购单（po_type=material）不需要客户信息
    // 普通采购单如果没有客户信息则必须选择
    if (poType === 'material' || !hasSupplier) {
      // 物料采购单，跳过客户验证
    } else if (!effectiveClientId || effectiveClientId === 'global') {
      newErrors.client_id = '请选择客户（收货库存必须分配到客户）';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log('=== HANDLE SUBMIT CLICKED ===');
    console.log('formData:', JSON.stringify(formData, null, 2));
    console.log('purchaseOrder:', purchaseOrder);
    console.log('selectedClient:', selectedClient);
    console.log('selectedClientId:', selectedClientId);
    
    if (!validateForm()) {
      console.log('Validation failed, errors:', errors);
      return;
    }

    setLoading(true);
    try {
      const receivedItems = formData.receipt_items
        .filter(item => (parseFloat(item.received_quantity) || 0) > 0)
        .map(item => ({
          product_code: item.product_code,
          product_id: item.product_id,
          _id: item._id || null,  // MongoDB ObjectId
          product_name: item.product_name,
          quantity_received: parseFloat(item.received_quantity) || 0,
          unit_cost: parseFloat(item.unit_cost) || 0,
          condition: item.condition || 'good',
          location: item.location || '',
          batch_number: item.batch_number || '',
          expiry_date: item.expiry_date || '',
          notes: item.notes || ''
        }));

      const submitData = {
        receipt_date: formData.receipt_date,
        items: receivedItems,  // Backend expects 'items'
        received_items: receivedItems,  // Also keep for compatibility
        notes: formData.receipt_note || '',
        receipt_status: validation.isComplete ? 'complete' : 'partial',
        inspector_name: formData.inspector_name || ''
      };

      if (selectedClient) {
        submitData.client_id = selectedClient.client_id || selectedClient.client_code || selectedClient._id;
        submitData.client_ref = selectedClient._id;
        submitData.client_name = selectedClient.client_name || selectedClient.name || '';
      } else if (selectedClientId && selectedClientId !== 'global') {
        submitData.client_id = selectedClientId;
      }

      console.log('=== RECEIPT SUBMIT DEBUG ===');
      console.log('Full formData:', formData);
      console.log('Inspector name from formData:', formData.inspector_name);
      console.log('Submit data inspector_name:', submitData.inspector_name);
      console.log('Client info:', {
        client_id: submitData.client_id,
        client_ref: submitData.client_ref,
        client_name: submitData.client_name
      });
      console.log('PO client info:', {
        client_id: purchaseOrder?.client_id,
        client_ref: purchaseOrder?.client_ref,
        client_name: purchaseOrder?.client_name
      });
      console.log('Full submitData:', JSON.stringify(submitData, null, 2));
      console.log('=== END DEBUG ===');

      await onSubmit(submitData);
      handleClose();
      window.location.reload();
    } catch (error) {
      console.error('Error submitting receipt:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save receipt' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        receipt_date: new Date().toISOString().split('T')[0],
        receipt_note: '',
        receipt_status: 'partial',
        inspector_name: '',
        receipt_items: []
      });
      setErrors({});
      setValidation({ totalReceived: 0, totalOrdered: 0, isComplete: false });
      onClose();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };


  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            {mode === 'view' ? '查看收货记录' : (mode === 'edit' ? '编辑收货记录' : '新建收货记录')}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {errors.submit && (
            <Alert variant="destructive">
              <AlertDescription>{errors.submit}</AlertDescription>
            </Alert>
          )}

          {errors.general && (
            <Alert variant="destructive">
              <AlertDescription>{errors.general}</AlertDescription>
            </Alert>
          )}

          {/* Purchase Order Info */}
          {purchaseOrder && (
            <Alert className="bg-primary/10 border-primary/20">
              <AlertDescription>
                采购单号: <strong>{purchaseOrder.purchase_order_id}</strong> |
                供应商: <strong>{purchaseOrder.supplier_name}</strong> |
                订单总额: <strong>{formatCurrency(purchaseOrder.total_cost)}</strong>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-base font-semibold mb-2">收货基本信息</h3>
              <Separator className="mb-4" />
            </div>

            {/* Show selected client info if from parent or PO */}
            {(selectedClientId || selectedClient || purchaseOrder?.client_name) && (
              <Alert className="bg-primary/10 border-primary/20">
                <AlertDescription>
                  客户: <strong>
                    {selectedClient?.client_name || selectedClient?.name || purchaseOrder?.client_name || selectedClientId}
                  </strong>
                </AlertDescription>
              </Alert>
            )}

            {/* Show warning if no client info available */}
            {!selectedClientId && !selectedClient && !purchaseOrder?.client_name && (
              <Alert variant="destructive" className="bg-warning/10 border-warning/20">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-warning">
                  <strong>警告:</strong> 采购订单缺少客户信息，无法创建收货记录。请先编辑采购订单添加客户信息。
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">收货日期 *</label>
                <Input
                  type="date"
                  value={formData.receipt_date}
                  onChange={(e) => handleFieldChange('receipt_date', e.target.value)}
                  className={errors.receipt_date ? 'border-red-500' : ''}
                  disabled={isViewMode}
                />
                {errors.receipt_date && (
                  <p className="text-xs text-destructive">{errors.receipt_date}</p>
                )}
              </div>
            </div>

            {/* Show inspector info as read-only */}
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                验收人员: <strong>{formData.inspector_name || '加载中...'}</strong>
                <span className="ml-1 italic text-xs">(自动设置为当前登录用户)</span>
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">收货备注</label>
              <Textarea
                value={formData.receipt_note}
                onChange={(e) => handleFieldChange('receipt_note', e.target.value)}
                placeholder="记录收货过程中的特殊情况或注意事项..."
                disabled={isViewMode}
                rows={2}
              />
            </div>

            {/* Validation Summary */}
            <div className="flex gap-2 mb-2 flex-wrap items-center">
              <Badge
                variant={validation.isComplete ? 'default' : 'secondary'}
                className={`flex items-center gap-1 ${validation.isComplete ? 'bg-success/100' : 'bg-warning/100'}`}
              >
                {validation.isComplete ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                收货进度: {validation.totalReceived}/{validation.totalOrdered} ({validation.fulfillmentRate?.toFixed(1)}%)
              </Badge>
              {!isViewMode && (
                <>
                  <Button variant="outline" size="sm" onClick={() => handleQuickFill('full')}>
                    全部收货
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickFill('zero')}>
                    清空数量
                  </Button>
                </>
              )}
            </div>

            {/* Receipt Items Table */}
            <div>
              <h3 className="text-base font-semibold mb-2">收货明细</h3>
              <Separator className="mb-4" />

              {errors.receipt_items && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{errors.receipt_items}</AlertDescription>
                </Alert>
              )}

              <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted border-b">
                    <tr>
                      <th className="px-3 py-2 text-center font-medium w-12">序号</th>
                      <th className="px-3 py-2 text-left font-medium">产品信息</th>
                      <th className="px-3 py-2 text-right font-medium">订购数量</th>
                      <th className="px-3 py-2 text-right font-medium">已收数量</th>
                      <th className="px-3 py-2 text-right font-medium">剩余数量</th>
                      <th className="px-3 py-2 text-right font-medium">本次收货 *</th>
                      <th className="px-3 py-2 text-right font-medium">单价</th>
                      <th className="px-3 py-2 text-right font-medium">小计</th>
                      <th className="px-3 py-2 text-left font-medium">货物状态</th>
                      <th className="px-3 py-2 text-left font-medium">仓库位置 *</th>
                      <th className="px-3 py-2 text-left font-medium">批次号</th>
                      <th className="px-3 py-2 text-left font-medium">备注</th>
                    </tr>
                  </thead>
                  <tbody>
                    {console.log('Rendering table with formData.receipt_items:', formData.receipt_items)}
                    {console.log('formData.receipt_items length:', formData.receipt_items?.length)}
                    {(formData.receipt_items || []).map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="px-3 py-2 text-center font-medium text-muted-foreground">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2">
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-xs text-muted-foreground">编码: {item.product_code || '-'}</p>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {item.ordered_quantity}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {item.previously_received || 0}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${item.remaining_quantity <= 0 ? 'text-destructive' : 'text-success'}`}>
                          {item.remaining_quantity || 0}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Input
                            type="number"
                            value={item.received_quantity}
                            onChange={(e) => handleItemChange(index, 'received_quantity', parseFloat(e.target.value) || 0)}
                            className={`w-20 ${errors[`item_received_quantity_${index}`] ? 'border-red-500' : ''}`}
                            disabled={isViewMode}
                            min={0}
                            step={1}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(item.unit_cost)}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatCurrency(item.line_total || 0)}
                        </td>
                        <td className="px-3 py-2">
                          <Select
                            value={item.condition || 'good'}
                            onValueChange={(value) => handleItemChange(index, 'condition', value)}
                            disabled={isViewMode}
                          >
                            <SelectTrigger className={`w-24 ${errors[`item_condition_${index}`] ? 'border-red-500' : ''}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="good">完好</SelectItem>
                              <SelectItem value="damaged">轻微损坏</SelectItem>
                              <SelectItem value="defective">严重缺陷</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          <div className="space-y-1">
                            <Input
                              value={item.location || ''}
                              onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                              placeholder="仓库位置 *"
                              className={`w-28 ${errors[`item_location_${index}`] ? 'border-red-500' : ''}`}
                              disabled={isViewMode}
                            />
                            {errors[`item_location_${index}`] && (
                              <p className="text-xs text-destructive">{errors[`item_location_${index}`]}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={item.batch_number || ''}
                            onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)}
                            placeholder="批次"
                            disabled={isViewMode}
                            className="w-24"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={item.notes || ''}
                            onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                            placeholder="备注"
                            disabled={isViewMode}
                            className="w-28"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {isViewMode ? '关闭' : '取消'}
          </Button>
          {!isViewMode && (
            <Button
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
              {loading ? '保存中...' : (mode === 'edit' ? '更新收货记录' : '创建收货记录')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseReceiptDialog;
